<script setup lang="ts">
import { computed, ref } from 'vue';
import Avatar from 'primevue/avatar';
import { useStringFunctions } from '@apparatix/composables';
import type { DashboardEmail } from '@apparatix/schemas/DashboardEmails';
import EmailMessageSummary from './EmailMessageSummary.vue';
import EmailMessageDetail from './EmailMessageDetail.vue';

type Props = {
  email: DashboardEmail,
  expanded?: boolean,
  showBorder?: boolean,
};

type Emits = {
  (e: 'toggle-detail', emailId: number, showDetail: boolean): void,
};

const props = defineProps<Props>();
const emit = defineEmits<Emits>();

const { getInitials, isEmptyString } = useStringFunctions();

const inlineBody = ref<string>();
const hasLoadedInlineBody = ref(false);

const isShowingDetail = computed({
  get() {
    return props.expanded;
  },
  set(isExpanded) {
    emit('toggle-detail', props.email.id, isExpanded);
  },
});

const primaryParticipant = computed(() => props.email?.participants?.from?.[0]);

const handleContentLoaded = (emailId: number, body?: string) => {
  if (emailId !== props.email.id) {
    return;
  }

  inlineBody.value = body;
  hasLoadedInlineBody.value = true;
};

const avatarInitials = computed(() => {
  if (isEmptyString(primaryParticipant.value?.name, true) || primaryParticipant.value.name === primaryParticipant.value.email) {
    return primaryParticipant.value.email.substring(0, 2);
  }

  return getInitials(primaryParticipant.value.name);
});
</script>
<template>
  <div class="flex gap-0 lg:gap-5 p-6" :class="{'border-b border-[var(--p-content-border-color)]': props.showBorder}">
    <div class="flex-none leading-none collapse lg:visible">
      <Avatar size="large" shape="circle" :label="avatarInitials" />
    </div>
    <EmailMessageSummary
      v-if="!isShowingDetail"
      :primary-participant="primaryParticipant"
      :email-id="props.email.id"
      :sent-at="props.email.externalReceivedAt"
      :snippet="props.email.snippet"
      @expand-click="isShowingDetail = true"
    />
    <EmailMessageDetail
      v-if="isShowingDetail"
      :email="props.email"
      :has-loaded-inline-body="hasLoadedInlineBody"
      :inline-body="inlineBody"
      @collapse-click="isShowingDetail = false"
      @content-loaded="handleContentLoaded"
    />
  </div>
</template>
